import { Component, computed, inject, input, signal } from '@angular/core';
import { SlicePipe } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Fluid } from 'primeng/fluid';
import { Textarea } from 'primeng/textarea';
import { Select } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { Dialog } from 'primeng/dialog';
import { Tooltip } from 'primeng/tooltip';
import { Tag } from 'primeng/tag';
import { ScrollPanel } from 'primeng/scrollpanel';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { SiteContentService } from '@/app/services/site-content.service';
import { RealmService } from '@/app/services/realm.service';
import { ApiService } from '@/app/services/api.service';
import { NotificationService } from '@/app/services/notification.service';
import { handleApiError } from '@/app/utils/api-error-handler';
import { onGlobalFilter } from '@/app/utils/table-utils';
import * as contentApi from '@/lib-backend/methods/content';
import { FaqItem } from '@/lib-backend';

@Component({
    selector: 'app-website-info-faq',
    standalone: true,
    imports: [
        SlicePipe, FormsModule, Button, InputText, InputNumber, Fluid,
        Textarea, Select, TableModule, IconField, InputIcon, Dialog,
        Tooltip, Tag, ScrollPanel, PrimeTemplate, Toast,
    ],
    providers: [MessageService],
    templateUrl: './faq-tab.html',
})
export class FaqTab {
    readonly realmId = input.required<string>();

    protected readonly siteContentService = inject(SiteContentService);
    protected readonly realmService = inject(RealmService);
    private readonly router = inject(Router);
    private readonly apiService = inject(ApiService);
    private readonly messageService = inject(MessageService);
    private readonly notification = inject(NotificationService);

    protected readonly importableRealms = computed(() =>
        this.realmService
            .realms()
            .filter((r) => r.RealmId !== this.realmId())
            .map((r) => ({
                label: `${r.DisplayName || r.Domain} (${r.Domain})`,
                value: r.RealmId,
            })),
    );

    // CRUD dialog
    protected dialogVisible = signal(false);
    protected dialogMode = signal<'edit' | 'add'>('add');
    protected editId = signal('');
    protected editCategory = signal('');
    protected editTitle = signal('');
    protected editMessage = signal('');
    protected editDisplayOrder = signal<number | null>(null);
    protected saving = signal(false);
    protected saveError = signal<string | null>(null);

    // Import dialog
    protected importDialogVisible = signal(false);
    protected importSourceRealmId = signal<string | null>(null);
    protected importSourceItems = signal<FaqItem[]>([]);
    protected importSourceLoading = signal(false);
    protected importSelectedItems: FaqItem[] = [];
    protected importing = signal(false);

    // Preview dialog
    protected previewDialogVisible = signal(false);
    protected previewItem = signal<FaqItem | null>(null);

    // Compare dialog
    protected compareDialogVisible = signal(false);
    protected compareOriginItem = signal<FaqItem | null>(null);
    protected compareNewItem = signal<FaqItem | null>(null);

    protected readonly onGlobalFilter = onGlobalFilter;

    protected openAddDialog(): void {
        this.dialogMode.set('add');
        this.editId.set('');
        this.editCategory.set('');
        this.editTitle.set('');
        this.editMessage.set('');
        this.editDisplayOrder.set(null);
        this.saveError.set(null);
        this.dialogVisible.set(true);
    }

    protected async openEditDialog(faqId: string): Promise<void> {
        const item = await this.siteContentService.getFaqItem(this.realmId(), faqId);
        if (item) {
            this.dialogMode.set('edit');
            this.editId.set(item.Id);
            this.editCategory.set(item.Category);
            this.editTitle.set(item.Title);
            this.editMessage.set(item.Message);
            this.editDisplayOrder.set(item.DisplayOrder);
            this.saveError.set(null);
            this.dialogVisible.set(true);
        }
    }

    protected async saveItem(): Promise<void> {
        this.saving.set(true);
        this.saveError.set(null);
        try {
            if (this.dialogMode() === 'add') {
                const result = await this.siteContentService.addFaqItem({
                    RealmId: this.realmId(),
                    Category: this.editCategory(),
                    Title: this.editTitle(),
                    Message: this.editMessage(),
                    DisplayOrder: this.editDisplayOrder(),
                });
                if (!result) {
                    this.saveError.set('Failed to create FAQ item');
                    return;
                }
            } else {
                const result = await this.siteContentService.updateFaqItem({
                    RealmId: this.realmId(),
                    FaqId: this.editId(),
                    Title: this.editTitle(),
                    Message: this.editMessage(),
                    Category: this.editCategory(),
                    DisplayOrder: this.editDisplayOrder(),
                });
                if (result !== 'Ok') {
                    this.saveError.set(result === 'NotFound' ? 'FAQ item not found' : 'Save failed');
                    return;
                }
            }
            this.dialogVisible.set(false);
        } finally {
            this.saving.set(false);
        }
    }

    protected openImportDialog(): void {
        this.realmService.loadRealms();
        this.importDialogVisible.set(true);
        this.importSourceRealmId.set(null);
        this.importSourceItems.set([]);
        this.importSelectedItems = [];
    }

    protected async onImportRealmChange(realmId: string | null): Promise<void> {
        this.importSourceRealmId.set(realmId);
        this.importSelectedItems = [];
        if (!realmId) {
            this.importSourceItems.set([]);
            return;
        }
        this.importSourceLoading.set(true);
        try {
            const listResp = await this.apiService.client.call(
                contentApi.listFaqs({ RealmId: realmId, Category: null })
            );
            const fullItems = await Promise.all(
                listResp.Items.map(async (summary) => {
                    const resp = await this.apiService.client.call(
                        contentApi.getFaqItem({ RealmId: realmId, FaqId: summary.Id })
                    );
                    return resp.Result === 'Ok' ? resp.Item : ({ ...summary, Message: '' } as FaqItem);
                })
            );
            this.importSourceItems.set(fullItems);
        } catch (e) {
            handleApiError(e, this.router, this.notification);
        } finally {
            this.importSourceLoading.set(false);
        }
    }

    protected getImportStatus(item: FaqItem): 'new' | 'override' {
        return this.siteContentService.faqItems().some(
            f => f.Category.toLowerCase() === item.Category.toLowerCase() &&
                 f.Title.toLowerCase() === item.Title.toLowerCase()
        ) ? 'override' : 'new';
    }

    protected openPreviewDialog(item: FaqItem): void {
        this.previewItem.set(item);
        this.previewDialogVisible.set(true);
    }

    protected openCompareDialog(item: FaqItem): void {
        const match = this.siteContentService.faqItems().find(
            f => f.Category.toLowerCase() === item.Category.toLowerCase() &&
                 f.Title.toLowerCase() === item.Title.toLowerCase()
        ) ?? null;
        this.compareOriginItem.set(match);
        this.compareNewItem.set(item);
        this.compareDialogVisible.set(true);
    }

    protected async executeImport(): Promise<void> {
        if (!this.importSourceRealmId() || !this.importSelectedItems.length) return;

        const targetRealmId = this.realmId();
        this.importing.set(true);
        let successCount = 0;
        let errorCount = 0;

        for (const item of [...this.importSelectedItems]) {
            const isOverride = this.getImportStatus(item) === 'override';

            if (isOverride) {
                const match = this.siteContentService.faqItems().find(
                    f => f.Category.toLowerCase() === item.Category.toLowerCase() &&
                         f.Title.toLowerCase() === item.Title.toLowerCase()
                );
                if (!match) { errorCount++; continue; }
                const result = await this.siteContentService.updateFaqItem({
                    RealmId: targetRealmId,
                    FaqId: match.Id,
                    Category: item.Category,
                    Title: item.Title,
                    Message: item.Message,
                    DisplayOrder: item.DisplayOrder,
                });
                if (result === 'Ok') successCount++;
                else errorCount++;
            } else {
                const result = await this.siteContentService.addFaqItem({
                    RealmId: targetRealmId,
                    Category: item.Category,
                    Title: item.Title,
                    Message: item.Message,
                    DisplayOrder: item.DisplayOrder,
                });
                if (result) successCount++;
                else errorCount++;
            }
        }

        await this.siteContentService.loadFaqsForRealm(targetRealmId);
        this.importing.set(false);

        if (successCount > 0) {
            this.messageService.add({
                severity: 'success',
                summary: 'Import complete',
                detail: `${successCount} FAQ item(s) imported successfully.`,
            });
        }
        if (errorCount > 0) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Partial import',
                detail: `${errorCount} FAQ item(s) failed to import.`,
            });
        }

        this.importDialogVisible.set(false);
    }
}
